import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { FaEye, FaEyeSlash, FaLock } from 'react-icons/fa';
import { FiArrowRight, FiCheckCircle, FiMail, FiShield } from 'react-icons/fi';
import api from '../axios';
import { useAuth } from '../context/AuthContext';
import { resolvePostLoginRoute } from '../utils/onboarding';

const loginPoints = [
  'Sign in to continue setup or return to your account area.',
  'Account status determines the next step after sign-in.',
  'Verify your email first if this is your first time signing in.',
];

function Login() {
  const [formData, setFormData] = useState({
    email: '',
    password: '',
  });
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const from = location.state?.from?.pathname || '/account';
  const { setAuth } = useAuth();

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    const { email, password } = formData;

    if (!email || !password) {
      setError('Please enter both email and password.');
      setIsLoading(false);
      return;
    }

    try {
      localStorage.removeItem('token');
      document.cookie = 'access_token=; Max-Age=0; path=/;';

      const response = await api.post(
        '/api/login',
        { email, password },
        {
          withCredentials: true,
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
            'X-Requested-With': 'XMLHttpRequest',
          },
          __isRetryRequest: true,
        }
      );

      if (response.status === 200 && response.data) {
        if (response.data.user) {
          localStorage.setItem('userEmail', response.data.user.email);
          localStorage.setItem('userId', response.data.user.id);
          localStorage.setItem('isLoggedIn', 'true');
        }

        const authToken = response.data.token || response.data.access_token;
        if (authToken) {
          localStorage.setItem('token', authToken);
        }

        const subscriptionStatus = response.data.subscription_status || 'inactive';

        setAuth({
          isAuthed: true,
          isLoading: false,
          user: response.data.user || { email: formData.email },
          subscriptionStatus,
        });

        const nextRoute = await resolvePostLoginRoute(api, subscriptionStatus, from);
        navigate(nextRoute, { replace: true });
      } else {
        const errorMessage =
          response.data?.detail?.message || response.data?.message || 'Login failed. Please try again.';
        setError(errorMessage);
      }
    } catch (err) {
      let errorMessage = 'Login failed. Please try again.';

      if (err.response) {
        const { status, data } = err.response;

        if (data?.detail) {
          if (Array.isArray(data.detail)) {
            errorMessage = data.detail[0]?.msg || data.detail[0] || 'Login failed.';
          } else if (typeof data.detail === 'string') {
            errorMessage = data.detail;
          } else if (typeof data.detail === 'object' && data.detail.message) {
            errorMessage = data.detail.message;
          }
        } else if (data?.message) {
          errorMessage = data.message;
        } else if (status === 400) {
          errorMessage = 'Invalid request. Please check your input.';
        } else if (status === 401) {
          errorMessage = 'Invalid email or password. If you have not registered, create an account first.';
        } else if (status === 403) {
          errorMessage =
            data?.detail?.message || data?.message || 'Please verify your email before logging in.';
        } else if (status === 429) {
          errorMessage = 'Too many login attempts. Please try again later.';
        } else if (status >= 500) {
          errorMessage = 'Server error. Please try again later.';
        }
      } else if (err.status === 422) {
        errorMessage = err.details || err.message || 'Login details are incomplete or invalid.';
      } else if (err.status === 403) {
        errorMessage = err.details || err.message || 'Please verify your email before logging in.';
      } else if (err.status === 401) {
        errorMessage = err.details || err.message || 'Invalid email or password. If you have not registered, create an account first.';
      } else if (err.request) {
        errorMessage = 'Unable to connect to the server. Please check your internet connection.';
      } else if (err.message) {
        errorMessage = err.message;
      }

      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen overflow-hidden bg-[#07111a] text-white">
      <div className="fixed inset-0 -z-20 bg-[radial-gradient(circle_at_top_left,_rgba(27,186,123,0.16),_transparent_28%),radial-gradient(circle_at_80%_20%,_rgba(45,122,255,0.16),_transparent_30%),linear-gradient(180deg,_#09131d_0%,_#05080c_100%)]" />

      <div className="mx-auto grid min-h-screen max-w-7xl gap-10 px-4 pb-12 pt-28 sm:px-6 lg:grid-cols-[0.95fr_1.05fr] lg:items-center lg:px-8 lg:pt-32">
        <section>
          <div className="inline-flex items-center gap-2 rounded-full border border-emerald-400/25 bg-emerald-400/10 px-4 py-2 text-sm font-semibold text-emerald-200">
            <FiShield className="h-4 w-4" />
            Secure account access
          </div>

          <h1 className="mt-6 max-w-3xl text-5xl font-black leading-[0.95] tracking-tight text-white sm:text-6xl lg:text-7xl">
            Log in and return
            <span className="block bg-gradient-to-r from-emerald-300 via-cyan-300 to-sky-400 bg-clip-text text-transparent">
              to your account
            </span>
          </h1>

          <p className="mt-6 max-w-2xl text-lg leading-8 text-slate-300 sm:text-xl">
            Sign in after email verification to continue setup, review your account, and access your user account.
          </p>

          <div className="mt-10 space-y-4">
            {loginPoints.map((point) => (
              <div key={point} className="flex items-start gap-3 rounded-2xl border border-white/10 bg-white/5 p-4 text-slate-300 backdrop-blur-xl">
                <FiCheckCircle className="mt-0.5 h-5 w-5 flex-shrink-0 text-emerald-300" />
                <span>{point}</span>
              </div>
            ))}
          </div>
        </section>

        <section className="rounded-[2rem] border border-white/10 bg-white/5 p-6 shadow-[0_30px_80px_rgba(0,0,0,0.35)] backdrop-blur-xl sm:p-8">
          <div className="flex items-center justify-between gap-4 border-b border-white/10 pb-5">
            <div>
              <p className="text-sm uppercase tracking-[0.24em] text-cyan-200/70">Account access</p>
              <h2 className="mt-2 text-3xl font-black text-white">Welcome back</h2>
            </div>
          </div>

          {error && (
            <div className="mt-6 rounded-2xl border border-rose-300/20 bg-rose-300/10 p-4 text-sm text-rose-200">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="mt-8 space-y-5">
            <div>
              <label htmlFor="email" className="mb-2 block text-sm font-medium text-slate-300">
                Email Address
              </label>
              <div className="relative">
                <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-4 text-slate-400">
                  <FiMail className="h-4 w-4" />
                </div>
                <input
                  id="email"
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleChange}
                  className="block w-full rounded-2xl border border-white/10 bg-slate-950/65 py-3 pl-11 pr-4 text-white placeholder-slate-500 outline-none transition focus:border-cyan-300/40"
                  placeholder="Enter your email"
                  required
                />
              </div>
            </div>

            <div>
              <label htmlFor="password" className="mb-2 block text-sm font-medium text-slate-300">
                Password
              </label>
              <div className="relative">
                <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-4 text-slate-400">
                  <FaLock className="h-4 w-4" />
                </div>
                <input
                  id="password"
                  name="password"
                  type={showPassword ? 'text' : 'password'}
                  value={formData.password}
                  onChange={handleChange}
                  className="block w-full rounded-2xl border border-white/10 bg-slate-950/65 py-3 pl-11 pr-12 text-white placeholder-slate-500 outline-none transition focus:border-cyan-300/40"
                  placeholder="••••••••"
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute inset-y-0 right-0 flex items-center pr-4 text-slate-400 transition hover:text-white"
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                >
                  {showPassword ? <FaEyeSlash /> : <FaEye />}
                </button>
              </div>
            </div>

            <button
              type="submit"
              disabled={isLoading}
              className="inline-flex w-full items-center justify-center gap-2 rounded-2xl bg-gradient-to-r from-emerald-300 to-cyan-300 px-6 py-4 font-bold text-slate-950 transition hover:scale-[1.01] disabled:opacity-70"
            >
              {isLoading ? 'Logging in...' : 'Log in'}
              {!isLoading && <FiArrowRight className="h-4 w-4" />}
            </button>
          </form>

          <div className="relative mt-8 mb-6">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-white/10" />
            </div>
            <div className="relative flex justify-center text-sm">
              <span className="bg-[#101a24] px-4 text-slate-400">New here?</span>
            </div>
          </div>

          <div className="space-y-4">
            <Link
              to="/register"
              className="inline-flex w-full items-center justify-center gap-2 rounded-2xl border border-white/12 bg-white/5 px-6 py-4 font-semibold text-white transition hover:bg-white/10"
            >
              Create account
            </Link>

            <div className="text-center">
              <Link to="/forgot-password" className="text-sm font-medium text-cyan-200 transition hover:text-white">
                Forgot your password?
              </Link>
            </div>
          </div>
        </section>
      </div>
    </div>
  );
}

export default Login;